import React, { useState } from "react";
import { Zap, Sun, Leaf, Compass, Cog, Wind, Battery, Droplets } from "lucide-react";
import EditableText from "./EditableText";
import { useContent } from "../context/ContentContext";

const ICON_MAP = { Zap, Sun, Leaf, Compass, Cog, Wind, Battery, Droplets };

export default function Solutions() {
  const { content } = useContent();
  const [active, setActive] = useState(0);
  const solutions = content?.solutions;
  if (!solutions) return null;

  const items = solutions.items ?? [];
  if (items.length === 0) return null;

  const idx = Math.min(active, items.length - 1);
  const current = items[idx];
  const CurrentIcon = ICON_MAP[current.icon] ?? Zap;

  return (
    <section
      id="solutions"
      className="relative py-24 md:py-32 bg-[#F9F8F6] border-t border-[#E2E0D8]"
      data-testid="solutions-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-24">
          <div className="md:col-span-7">
            <EditableText
              path="solutions.overline"
              value={solutions.overline}
              as="div"
              className="text-[#4A4F6B] uppercase text-xs tracking-[0.3em] font-semibold mb-6"
              testId="solutions-overline"
            />
            <EditableText
              path="solutions.heading"
              value={solutions.heading}
              as="h2"
              multiline
              className="font-serif text-[#00025C] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold text-balance"
              testId="solutions-heading"
            />
          </div>
          <div className="md:col-span-4 md:col-start-9 md:pt-6">
            <EditableText
              path="solutions.intro"
              value={solutions.intro}
              as="p"
              multiline
              className="text-[#1A1D1A]/80 text-base md:text-lg leading-relaxed"
              testId="solutions-intro"
            />
          </div>
        </div>

        {/* Desktop: Left tab index + Right display panel */}
        <div className="hidden md:grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Index */}
          <nav className="md:col-span-5 lg:col-span-4" data-testid="solutions-tab-list">
            <div className="border-t border-[#E2E0D8]">
              {items.map((item, i) => {
                const isActive = i === idx;
                const Icon = ICON_MAP[item.icon] ?? Zap;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActive(i)}
                    className={`group relative w-full text-left border-b border-[#E2E0D8] py-6 lg:py-7 transition-all duration-300 ${
                      isActive ? "pl-5" : "hover:pl-3 opacity-55 hover:opacity-100"
                    }`}
                    data-testid={`solution-tab-${i + 1}`}
                    aria-pressed={isActive}
                  >
                    <span
                      aria-hidden
                      className={`absolute left-0 top-5 bottom-5 w-[3px] bg-[#00025C] transition-opacity duration-300 ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <div className="flex items-center gap-5">
                      <span
                        className={`font-serif text-2xl lg:text-3xl tabular-nums font-semibold transition-colors ${
                          isActive ? "text-[#00025C]" : "text-[#4A4F6B]"
                        }`}
                      >
                        {item.number}
                      </span>
                      <div
                        className={`flex items-center gap-3 transition-colors ${
                          isActive ? "text-[#00025C]" : "text-[#1A1D1A]"
                        }`}
                      >
                        <Icon size={18} strokeWidth={2} />
                        <span className="font-serif text-xl lg:text-2xl font-semibold">
                          {item.title}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Display panel */}
          <div className="md:col-span-7 lg:col-span-7 lg:col-start-6 relative min-h-[460px]">
            {/* Giant ghosted numeral — navy watermark */}
            <div
              aria-hidden
              className="pointer-events-none absolute -top-6 md:-top-10 right-0 font-serif leading-none select-none text-[180px] md:text-[240px] lg:text-[300px] text-[#00025C]/[0.055] font-semibold"
              key={`ghost-${idx}`}
            >
              {current.number}
            </div>

            <div key={`panel-${idx}`} className="relative fade-swap">
              <div className="flex items-center gap-4 mb-10">
                <span
                  className="flex items-center justify-center w-14 h-14 bg-[#00025C] text-[#F9F8F6]"
                  aria-hidden
                >
                  <CurrentIcon size={22} strokeWidth={1.75} />
                </span>
                <div className="flex items-center gap-3 text-[#00025C] uppercase text-[11px] tracking-[0.3em] font-bold">
                  <span>{current.number}</span>
                  <span className="w-6 h-px bg-[#00025C]/40" />
                  <span>Solution</span>
                </div>
              </div>

              <EditableText
                path={`solutions.items.${idx}.title`}
                value={current.title}
                as="h3"
                multiline
                className="font-serif text-[#1A1D1A] text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight font-semibold mb-10 max-w-2xl"
                testId={`solution-${idx + 1}-title`}
              />

              <div className="w-20 h-[2px] bg-[#00025C] mb-10" />

              <div className="space-y-6 text-[#1A1D1A]/85 max-w-2xl">
                <EditableText
                  path={`solutions.items.${idx}.paragraph1`}
                  value={current.paragraph1}
                  as="p"
                  multiline
                  className="text-sm md:text-[15px] leading-[1.75]"
                  testId={`solution-${idx + 1}-paragraph-1`}
                />
                <EditableText
                  path={`solutions.items.${idx}.paragraph2`}
                  value={current.paragraph2}
                  as="p"
                  multiline
                  className="text-sm md:text-[15px] leading-[1.75]"
                  testId={`solution-${idx + 1}-paragraph-2`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: stacked cards (all content visible) */}
        <div className="md:hidden space-y-12">
          {items.map((item, i) => {
            const Icon = ICON_MAP[item.icon] ?? Zap;
            return (
              <article
                key={item.id}
                className="relative border-t border-[#E2E0D8] pt-8"
                data-testid={`solution-card-mobile-${i + 1}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="flex items-center justify-center w-11 h-11 bg-[#00025C] text-[#F9F8F6]">
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <EditableText
                    path={`solutions.items.${i}.number`}
                    value={item.number}
                    as="span"
                    className="font-serif text-[#00025C] text-2xl font-semibold"
                    testId={`solution-m-${i + 1}-number`}
                  />
                </div>
                <EditableText
                  path={`solutions.items.${i}.title`}
                  value={item.title}
                  as="h3"
                  multiline
                  className="font-serif text-[#1A1D1A] text-3xl leading-[1.1] tracking-tight font-semibold mb-5"
                  testId={`solution-m-${i + 1}-title`}
                />
                <div className="w-12 h-[2px] bg-[#00025C] mb-5" />
                <div className="space-y-4 text-[#1A1D1A]/80">
                  <EditableText
                    path={`solutions.items.${i}.paragraph1`}
                    value={item.paragraph1}
                    as="p"
                    multiline
                    className="text-sm leading-[1.75]"
                    testId={`solution-m-${i + 1}-paragraph-1`}
                  />
                  <EditableText
                    path={`solutions.items.${i}.paragraph2`}
                    value={item.paragraph2}
                    as="p"
                    multiline
                    className="text-sm leading-[1.75]"
                    testId={`solution-m-${i + 1}-paragraph-2`}
                  />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
